import { useState, useMemo } from 'react'
import { DetailLevelToggle } from './DetailLevelToggle'
import { GanttRow } from './GanttRow'
import { CrewLoadSparkline } from './CrewLoadSparkline'
import { ComingSoonOverlay } from './ComingSoonOverlay'
import type { DetailLevel, MovementCallbacks } from './types'
import type { ScheduleItem } from './useTodayBoardData'

interface GanttViewProps extends MovementCallbacks {
  movements: ScheduleItem[]
}

export function GanttView({ movements }: GanttViewProps) {
  const [detailLevel, setDetailLevel] = useState<DetailLevel>('L1')

  // Compute time axis bounds from data ± 1hr padding
  const { startHour, totalHours, hours } = useMemo(() => {
    let earliest = 8 // default
    let latest = 18

    for (const m of movements) {
      const t = m.scheduledTime || m.sortTime
      if (t) {
        const h = new Date(t).getHours()
        if (h < earliest) earliest = h
        if (h > latest) latest = h
      }
    }

    const s = Math.max(0, earliest - 1)
    const e = Math.min(23, latest + 2)
    const total = e - s + 1
    const hrs = Array.from({ length: total }, (_, i) => s + i)

    return { startHour: s, totalHours: total, hours: hrs }
  }, [movements])

  // NOW line position
  const nowPercent = useMemo(() => {
    const now = new Date()
    const minutesSinceStart = (now.getHours() - startHour) * 60 + now.getMinutes()
    const pct = (minutesSinceStart / (totalHours * 60)) * 100
    return pct >= 0 && pct <= 100 ? pct : null
  }, [startHour, totalHours])

  if (movements.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm py-12">
        No movements scheduled for this day
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-card rounded-xl border overflow-hidden shadow-sm">
      {/* Sticky header: controls + time axis */}
      <div className="border-b bg-muted/30 sticky top-0 z-20">
        {/* Controls */}
        <div className="flex items-center justify-between px-3 py-1.5 border-b">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Timeline</span>
          <DetailLevelToggle value={detailLevel} onChange={setDetailLevel} />
        </div>

        {/* Time axis */}
        <div className="flex items-center">
          <div className="w-[300px] shrink-0 p-2 border-r font-semibold text-muted-foreground text-xs flex items-center justify-between">
            <span>Operation</span>
            <span className="text-[10px] font-medium">Loc / Client</span>
          </div>
          <div className="flex-1 flex items-center relative h-10">
            {hours.map((h) => (
              <div
                key={h}
                className="flex-1 border-l h-full flex items-end pb-1 pl-1 text-[10px] font-mono font-medium text-muted-foreground"
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}

            {/* NOW line */}
            {nowPercent !== null && (
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-blue-500 z-30 pointer-events-none"
                style={{ left: `${nowPercent}%` }}
              >
                <div className="absolute -top-0.5 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded flex items-center gap-1 shadow-sm">
                  <div className="w-1 h-1 rounded-full bg-white animate-pulse" />
                  NOW
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto relative z-10">
        {/* Background grid */}
        <div className="absolute inset-0 flex ml-[300px] pointer-events-none z-0">
          {hours.map((h) => (
            <div key={h} className="flex-1 border-l border-border/30 h-full" />
          ))}
        </div>

        {/* NOW line extended */}
        {nowPercent !== null && (
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-blue-500/30 z-5 pointer-events-none"
            style={{ left: `calc(300px + ${nowPercent}% * (100% - 300px) / 100%)` }}
          />
        )}

        {movements.map((m) => (
          <GanttRow
            key={`${m.movementType}-${m.allocationId}`}
            item={m}
            detailLevel={detailLevel}
            startHour={startHour}
            totalHours={totalHours}
          />
        ))}
      </div>

      {/* Crew load sparkline (mock + overlay) */}
      <div className="relative">
        <CrewLoadSparkline startHour={startHour} totalHours={totalHours} />
        <ComingSoonOverlay level="L2" featureName="Crew Load" />
      </div>
    </div>
  )
}
